import { useState, useEffect, useCallback, RefObject } from "react";

interface UseOverflowOptions {
  direction?: "horizontal" | "vertical" | "both";
  paddingAware?: boolean; // subtract padding if true
}

export function useOverflowDetector<T extends HTMLElement>(
  containerRef: RefObject<T>,
  contentRef: RefObject<HTMLElement>,
  { direction = "horizontal", paddingAware = true }: UseOverflowOptions = {}
) {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    let containerWidth = container.clientWidth;
    let containerHeight = container.clientHeight;

    if (paddingAware) {
      const style = window.getComputedStyle(container);

      const padX =
        parseFloat(style.paddingLeft || "0") +
        parseFloat(style.paddingRight || "0");
      const padY =
        parseFloat(style.paddingTop || "0") +
        parseFloat(style.paddingBottom || "0");

      containerWidth -= padX;
      containerHeight -= padY;
    }

    let overflow = false;
    if (direction === "horizontal")
      overflow = content.scrollWidth > containerWidth;
    else if (direction === "vertical")
      overflow = content.scrollHeight > containerHeight;
    else
      overflow =
        content.scrollWidth > containerWidth ||
        content.scrollHeight > containerHeight;

    setIsOverflowing(overflow);
  }, [direction, paddingAware, containerRef, contentRef]);

  useEffect(() => {
    checkOverflow();

    window.addEventListener("resize", checkOverflow);

    // React to dynamic changes in size
    const resizeObserver = new ResizeObserver(() => checkOverflow());
    if (contentRef.current) resizeObserver.observe(contentRef.current);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", checkOverflow);
      resizeObserver.disconnect();
    };
  }, [checkOverflow]);

  return isOverflowing;
}
